import type {AqForecastHourly} from '../../api/types';
import {AQI_BANDS} from './bands';
import {isFiniteNumber} from './pollutants';

const HOUR = 3600;
/** Room above the highest point, so the line never runs along the top edge. */
const HEADROOM = 1.08;

export type AqBandZone = {bandIndex: number; low: number; high: number};

export type AqChartScale = {
  /** The axis top: a band edge, never a round number between two bands. */
  max: number;
  /** One per band up to `max`, contiguous from 0. */
  zones: AqBandZone[];
  ticks: number[];
};

/**
 * The hours an AQI forecast chart draws: from the current one (`aqSummary`'s
 * `current`) to `hours` after it, each with an AQI. Hours already past are
 * left out, as the peak line leaves them out.
 */
export const aqChartHours = (
  hourly: AqForecastHourly[],
  current: AqForecastHourly | undefined,
  hours = 24,
): AqForecastHourly[] => {
  if (!current) {
    return [];
  }
  const end = current.timestamp + hours * HOUR;
  return hourly.filter(
    (item) =>
      isFiniteNumber(item.AQ_AQI) &&
      item.timestamp >= current.timestamp &&
      item.timestamp <= end,
  );
};

/**
 * A y axis for AQI values that reads as the scale itself: it tops out at a
 * band edge, the bands below are zones, and the ticks sit on their edges.
 */
export const aqChartScale = (values: number[]): AqChartScale => {
  const highest = Math.max(0, ...values.filter(isFiniteNumber));
  const top =
    AQI_BANDS.find((band) => band.high >= highest * HEADROOM) ??
    AQI_BANDS[AQI_BANDS.length - 1];
  const max = top.high;

  const zones: AqBandZone[] = [];
  let low = 0;
  for (const [bandIndex, band] of AQI_BANDS.entries()) {
    if (low >= max) {
      break;
    }
    zones.push({bandIndex, low, high: band.high});
    low = band.high;
  }

  return {max, zones, ticks: [0, ...zones.map((zone) => zone.high)]};
};
